<div class="flex h-full min-h-0 w-full flex-col overflow-y-auto scrollbar-transparent">
  <div class="shrink-0 space-y-4 p-4">
    <div>
      <h4 class="text-sm font-semibold text-primary">Duplicate application cleanup</h4>
      <p class="mt-1 text-xs text-secondary">
        Search by brief ID and creator Firebase UID. When duplicates exist, delete the lower-progress row to
        remove its status history and application, then reset the valid application to Applied and release its
        wallet reservation so you can re-hire. GodMode only.
      </p>
    </div>

    <div class="grid w-full max-w-3xl gap-4 sm:grid-cols-2">
      @if (!settingsContext.hasBoth()) {
      <p class="sm:col-span-2 text-sm text-amber-800 dark:text-amber-200" role="status">
        Enter brief ID and creator Firebase UID in the context bar above, then search.
      </p>
      }

      <div class="sm:col-span-2">
        <button
          type="button"
          class="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          (click)="runSearch()"
          [disabled]="searchLoading() || !settingsContext.hasBoth()"
        >
          @if (searchLoading()) {
            Searching…
          } @else {
            Search applications
          }
        </button>
      </div>
    </div>

    @if (searchError()) {
    <p class="text-sm text-red-600 dark:text-red-400" role="alert">{{ searchError() }}</p>
    }
  </div>

  @if (lookupResult(); as result) {
  <div class="shrink-0 border-t border-zinc-200 p-4 dark:border-zinc-800">
    <div class="mb-4 flex flex-wrap items-center gap-2">
      <span class="text-sm font-medium text-primary">
        {{ result.applicationCount }} application{{ result.applicationCount === 1 ? '' : 's' }} found
      </span>
      @if (result.hasDuplicates) {
      <span
        class="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100"
      >
        Duplicates detected
      </span>
      } @else {
      <span
        class="rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium text-secondary dark:bg-zinc-800"
      >
        No duplicates
      </span>
      }
    </div>

    <ul class="flex flex-col gap-3">
      @for (app of result.applications; track app.id) {
      <li [class]="rowClass(app)">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0 flex-1 space-y-2">
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-sm font-semibold text-primary">
                {{ applicationStatusLabel(app.applicationStatus) }}
              </span>
              @if (app.isLowerProgressDuplicate) {
              <span
                class="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-medium text-amber-950 dark:bg-amber-800 dark:text-amber-50"
              >
                Lower progress — safe to delete
              </span>
              } @else if (app.isDuplicate) {
              <span
                class="rounded-full bg-emerald-200 px-2 py-0.5 text-xs font-medium text-emerald-950 dark:bg-emerald-900 dark:text-emerald-50"
              >
                Keep this application
              </span>
              }
            </div>

            <dl class="grid gap-1 text-xs text-secondary sm:grid-cols-2">
              <div>
                <dt class="inline font-medium text-primary">Application ID: </dt>
                <dd class="inline font-mono break-all">{{ app.id }}</dd>
              </div>
              <div>
                <dt class="inline font-medium text-primary">Status history rows: </dt>
                <dd class="inline">{{ app.statusHistory.length }}</dd>
              </div>
              <div>
                <dt class="inline font-medium text-primary">Created: </dt>
                <dd class="inline">{{ app.createdDate | date: 'medium' }}</dd>
              </div>
              <div>
                <dt class="inline font-medium text-primary">Status updated: </dt>
                <dd class="inline">{{ app.applicationStatusModifiedDate | date: 'medium' }}</dd>
              </div>
              @if (app.hiredEarnings != null) {
              <div>
                <dt class="inline font-medium text-primary">Hired earnings: </dt>
                <dd class="inline">{{ app.hiredEarnings }}</dd>
              </div>
              }
            </dl>

            <div class="rounded-lg border border-zinc-200/80 bg-zinc-50/80 p-3 text-xs dark:border-zinc-700 dark:bg-zinc-900/50">
              <p class="font-medium text-primary">Status history</p>
              @if (app.statusHistory.length) {
              <ul class="mt-2 space-y-1.5">
                @for (statusItem of app.statusHistory; track statusItem.id) {
                <li class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-secondary">
                  <span class="font-medium text-primary">{{ formatStatusDate(statusItem.createdDate) }}</span>
                  <span>{{ applicationStatusLabel(statusItem.status) }}</span>
                  <span class="text-zinc-500 dark:text-zinc-400">— {{ statusUserLabel(statusItem.user) }}</span>
                </li>
                }
              </ul>
              } @else {
              <p class="mt-1 text-secondary">No status history rows.</p>
              }
            </div>

            <div class="rounded-lg border border-zinc-200/80 bg-zinc-50/80 p-3 text-xs dark:border-zinc-700 dark:bg-zinc-900/50">
              <p class="font-medium text-primary">Wallet transaction</p>
              @if (app.walletTransaction; as wt) {
              <dl class="mt-1 grid gap-1 text-secondary sm:grid-cols-2">
                <div>
                  <dt class="inline font-medium text-primary">ID: </dt>
                  <dd class="inline font-mono break-all">{{ wt.id }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-primary">Amount: </dt>
                  <dd class="inline">{{ formatMoney(wt.amount, wt.currencyCode) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-primary">Type: </dt>
                  <dd class="inline">{{ walletTransactionTypeLabel(wt.type) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-primary">Status: </dt>
                  <dd class="inline">{{ walletTransactionStatusLabel(wt.status) }}</dd>
                </div>
                <div>
                  <dt class="inline font-medium text-primary">Reserved: </dt>
                  <dd class="inline">{{ wt.reservedForPurchase ? 'Yes' : 'No' }}</dd>
                </div>
                @if (wt.hasPayout) {
                <div class="sm:col-span-2 text-amber-700 dark:text-amber-300">
                  Linked payout — delete blocked until payout is resolved.
                </div>
                }
              </dl>
              } @else {
              <p class="mt-1 text-secondary">
                None found for this application ID (check row above) with matching creatorBlazeFirebaseUid and
                isDeleted = false.
              </p>
              }
            </div>
          </div>

          @if (app.canDelete) {
          <button
            type="button"
            class="shrink-0 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-800 hover:bg-red-100 disabled:opacity-50 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100 dark:hover:bg-red-950/60"
            (click)="deleteApplication(app)"
            [disabled]="deleteLoadingId() === app.id"
          >
            @if (deleteLoadingId() === app.id) {
              Deleting…
            } @else {
              Remove duplicate &amp; prepare re-hire
            }
          </button>
          }
        </div>
      </li>
      }
    </ul>
  </div>
  }
</div>
